/*
 * The early access request form and its review step.
 *
 * Split out of the landing page so the page itself stays a server component:
 * this is the only interactive part, and shipping the whole marketing page as
 * client JS to run one form is waste the reader pays for.
 *
 * The review step is a real <dialog> opened with showModal(). The stylesheet
 * ported from the mock styles `dialog` and `dialog::backdrop` and gives
 * .request-dialog no positioning of its own, so rendering it as a plain div
 * put the panel inline at the foot of the page with no backdrop. The element
 * also brings focus trapping, Escape, and the top layer for free.
 */
'use client'

import Link from 'next/link'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { sanitizeText } from '@/lib/sanitize'

const EMPTY_FORM = {
  name: '',
  email: '',
  role: '',
  website: '',
  goal: '',
  consent: false,
}

export function RequestAccessForm() {
  const [form, setForm] = useState(EMPTY_FORM)
  const [review, setReview] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDialogElement>(null)

  const set = useCallback(
    (field: keyof typeof EMPTY_FORM) =>
      (
        event: React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
      ) =>
        setForm(current => ({ ...current, [field]: event.target.value })),
    []
  )

  /*
   * The mock promises, in its own copy, that the request is reviewed before
   * anything leaves the page and that nothing is stored here. Building the
   * message in the browser and handing it to the person's mail client is what
   * keeps that promise true.
   *
   * Every field goes through sanitizeText first: it is the repo's one
   * sanitiser, and this text is echoed back into the review panel.
   */
  const onReview = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      const clean = (value: string) => sanitizeText(value).trim()
      setReview(
        [
          'Name: ' + clean(form.name),
          'Email: ' + clean(form.email),
          'Building as: ' + clean(form.role),
          form.website ? 'Current website: ' + clean(form.website) : null,
          form.goal ? '\nWhat they want to build:\n' + clean(form.goal) : null,
        ]
          .filter(Boolean)
          .join('\n')
      )
    },
    [form]
  )

  // showModal() cannot run during render, so the element is opened once the
  // review text exists and closed when it is cleared.
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (review !== null && !dialog.open) dialog.showModal()
    if (review === null && dialog.open) dialog.close()
  }, [review])

  // The hint returns after a moment so the line is never a stale status.
  useEffect(() => {
    if (!feedback) return
    const timer = window.setTimeout(() => setFeedback(null), 4000)
    return () => window.clearTimeout(timer)
  }, [feedback])

  const copy = useCallback(() => {
    if (!review) return
    void navigator.clipboard?.writeText(review).then(
      () => setFeedback('Request copied. Paste it into an email when ready.'),
      () => setFeedback('Could not copy. Select the text above and copy it.')
    )
  }, [review])

  // Same escape hatch the mock offers: a file to keep if the mail client is
  // not where this person works.
  const download = useCallback(() => {
    if (!review) return
    const url = URL.createObjectURL(
      new Blob([review], { type: 'text/plain;charset=utf-8' })
    )
    const link = document.createElement('a')
    link.href = url
    link.download = 'radar-sites-request.txt'
    link.click()
    URL.revokeObjectURL(url)
    setFeedback('Request downloaded.')
  }, [review])

  const mailto = useMemo(
    () =>
      'mailto:founders@pixelmojo.io?subject=' +
      encodeURIComponent('Radar Sites early access') +
      '&body=' +
      encodeURIComponent(review ?? ''),
    [review]
  )

  return (
    <>
      <form
        id='interest-form'
        className='interest-form'
        aria-labelledby='form-title'
        onSubmit={onReview}
      >
        <h3 id='form-title'>Tell us what you’re building.</h3>
        <p className='form-intro'>
          Leave a little context. You’ll review your request before opening it
          in your email app.
        </p>
        {/* The mock served this statically; the review step needs JS either way. */}
        <noscript>
          <p className='no-script-note'>
            For a reviewed request, enable JavaScript. You can also email{' '}
            <a href='mailto:founders@pixelmojo.io'>founders@pixelmojo.io</a>{' '}
            directly. This form does not add you to a signup list.
          </p>
        </noscript>
        <div className='form-grid'>
          <div className='field'>
            <label htmlFor='interest-name'>
              Your name <span>Required</span>
            </label>
            <input
              id='interest-name'
              name='name'
              autoComplete='name'
              placeholder='Your name'
              maxLength={100}
              required={true}
              value={form.name}
              onChange={set('name')}
            />
          </div>
          <div className='field'>
            <label htmlFor='interest-email'>
              Email address <span>Required</span>
            </label>
            <input
              id='interest-email'
              name='email'
              type='email'
              autoComplete='email'
              placeholder='you@company.com'
              maxLength={180}
              required={true}
              value={form.email}
              onChange={set('email')}
            />
          </div>
          <div className='field'>
            <label htmlFor='interest-role'>
              I’m building as <span>Required</span>
            </label>
            <select
              id='interest-role'
              name='role'
              required={true}
              value={form.role}
              onChange={set('role')}
            >
              <option value='' disabled={true}>
                Select your role
              </option>
              <option value='Agency or small team'>Agency or small team</option>
              <option value='Independent designer'>Independent designer</option>
              <option value='Business owner'>Business owner</option>
              <option value='Other'>Something else</option>
            </select>
          </div>
          <div className='field'>
            <label htmlFor='interest-website'>
              Current website <span>Optional</span>
            </label>
            <input
              id='interest-website'
              name='website'
              type='url'
              autoComplete='url'
              inputMode='url'
              placeholder='https://yourwebsite.com'
              maxLength={300}
              value={form.website}
              onChange={set('website')}
            />
          </div>
          <div className='field full-width'>
            <label htmlFor='interest-goal'>
              What would you like to build? <span>Optional</span>
            </label>
            <textarea
              id='interest-goal'
              name='goal'
              rows={3}
              maxLength={1200}
              placeholder='A new site, a client project, a move from another builder…'
              value={form.goal}
              onChange={set('goal')}
            />
          </div>
        </div>
        <label className='consent' htmlFor='interest-consent'>
          <input
            id='interest-consent'
            name='consent'
            type='checkbox'
            required={true}
            checked={form.consent}
            onChange={event =>
              setForm(f => ({ ...f, consent: event.target.checked }))
            }
          />
          <span>
            PixelMojo may contact me about Radar Sites and this request.{' '}
            <Link
              href='/privacy-policy'
              target='_blank'
              rel='noopener noreferrer'
            >
              Privacy policy
            </Link>
          </span>
        </label>
        <button className='button button-pink form-submit' type='submit'>
          Review my request
          <svg className='icon' aria-hidden='true'>
            <use href='#arrow-right' />
          </svg>
        </button>
        <p className='form-disclosure'>
          This page prepares an email. Nothing is sent or added to a signup list
          automatically, and your details are not saved on this page.
        </p>
        <p className='direct-contact'>
          Prefer a direct note?{' '}
          <a href='mailto:founders@pixelmojo.io'>
            founders@pixelmojo.io
            <svg className='icon' aria-hidden='true'>
              <use href='#arrow-up' />
            </svg>
          </a>
        </p>
      </form>

      {/*
        The mock's own dialog, class for class. Its stylesheet already styles
        request-top, request-instructions, request-recipient, the readonly
        summary textarea, both action rows and the live-region feedback line,
        so inventing a panel structure left all of it unstyled.
      */}
      <dialog
        ref={dialogRef}
        className='request-dialog'
        aria-labelledby='request-title'
        onClose={() => setReview(null)}
      >
        <div className='request-top'>
          <p className='eyebrow'>One last look</p>
          <button
            type='button'
            className='icon-button'
            aria-label='Close request review'
            onClick={() => setReview(null)}
          >
            <svg className='icon' aria-hidden='true'>
              <use href='#close' />
            </svg>
          </button>
        </div>
        <h2 id='request-title'>
          Your request.
          <br />
          Ready for review.
        </h2>
        <p className='request-instructions'>
          Check your details, then open an email draft. You will send it
          yourself. Nothing has been sent from this page.
        </p>
        <label htmlFor='request-summary' className='request-recipient'>
          Email to <strong>founders@pixelmojo.io</strong>
        </label>
        <textarea
          id='request-summary'
          readOnly
          rows={11}
          spellCheck={false}
          aria-label='Your early-access request'
          value={review ?? ''}
        />
        <div className='request-main-actions'>
          <a className='button button-pink' href={mailto}>
            Open email draft
            <svg className='icon' aria-hidden='true'>
              <use href='#mail' />
            </svg>
          </a>
          <button
            type='button'
            className='button button-outline'
            onClick={() => setReview(null)}
          >
            Edit details
          </button>
        </div>
        <div className='request-alt-actions'>
          <button type='button' className='small-action' onClick={copy}>
            <svg className='icon' aria-hidden='true'>
              <use href='#copy' />
            </svg>
            Copy request
          </button>
          <button type='button' className='small-action' onClick={download}>
            <svg className='icon' aria-hidden='true'>
              <use href='#download' />
            </svg>
            Download request
          </button>
        </div>
        {/* The mock's own live region. It carries the standing hint until an
            action replaces it, so the copy confirmation is announced as well
            as shown. */}
        <p className='request-feedback' role='status' aria-live='polite'>
          {feedback ??
            'Not using an email app? Copy or download the request and send it when you are ready.'}
        </p>
      </dialog>
    </>
  )
}
